import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatCurrencyEUR, formatDateTimeIT } from "@/lib/formatters";
import { AvailabilitySelector, isFiveMinuteTimeValue } from "./AvailabilitySelector";
import {
  businessLocalDateTimeToIso,
  isHistoricalAppointmentEnd,
  toBusinessDateTimeLocalValue,
} from "../lib/historicalAppointment";
import { toBusinessDateKey } from "../lib/agendaView";
import { fetchCustomerArchivePage } from "../lib/customerArchive";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

const SLOT_STEP_MINUTES = 5;
const DEFAULT_SUBMIT_ERROR = "Non riesco a creare l'appuntamento.";
const CUSTOMER_SEARCH_MIN_LENGTH = 2;

const getServiceStaffError = ({ service_id: serviceId, staff_id: staffId }) => {
  if (!serviceId && !staffId) return "Seleziona un servizio e un'operatrice.";
  if (!serviceId) return "Seleziona un servizio.";
  if (!staffId) return "Seleziona un'operatrice.";
  return "";
};

const normalizeCustomerSearch = (value) => String(value || "").trim().toLocaleLowerCase("it-IT");

const matchesCustomerSearch = (customer, searchTerm) => {
  const normalizedTerm = normalizeCustomerSearch(searchTerm);
  if (normalizedTerm.length < CUSTOMER_SEARCH_MIN_LENGTH) return false;

  const matchesText = [customer.name, customer.email, customer.phone]
    .map(normalizeCustomerSearch)
    .some((value) => value.includes(normalizedTerm));
  if (matchesText) return true;

  const phoneDigits = String(customer.phone || "").replace(/\D/g, "");
  const searchDigits = normalizedTerm.replace(/\D/g, "");
  return searchDigits.length >= CUSTOMER_SEARCH_MIN_LENGTH && phoneDigits.includes(searchDigits);
};

const getCustomerContact = (customer) => {
  const contacts = [customer.email, customer.phone].filter(Boolean);
  return contacts.length > 0 ? contacts.join(" · ") : "Nessun recapito disponibile";
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export function AppointmentWizardMobile({
  appointmentForm,
  resetToken,
  customerMode,
  customers,
  services,
  staff,
  quickCustomerForm,
  onFieldChange,
  onCustomerModeChange,
  onQuickCustomerChange,
  onSubmit,
  standalone = false,
  businessTimezone = "Europe/Rome",
  historicalOutcomeEnabled = false,
}) {
  const stepTitleRef = useRef(null);
  const customerSearchRef = useRef(null);
  const customerBrowseRequestRef = useRef(0);
  const shouldFocusCustomerSearchRef = useRef(false);
  const schedulingScopeRef = useRef(`${appointmentForm.service_id || ""}:${appointmentForm.staff_id || ""}`);
  const [step, setStep] = useState(1);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [customerBrowseOpen, setCustomerBrowseOpen] = useState(false);
  const [customerBrowseItems, setCustomerBrowseItems] = useState([]);
  const [customerBrowseLoading, setCustomerBrowseLoading] = useState(false);
  const [customerBrowseError, setCustomerBrowseError] = useState("");
  const [customerBrowseHasMore, setCustomerBrowseHasMore] = useState(false);
  const [suggestionDay, setSuggestionDay] = useState(() => toDateInputValue(new Date()));
  const [suggestedSlots, setSuggestedSlots] = useState([]);
  const [selectedSlotStartTime, setSelectedSlotStartTime] = useState("");
  const [schedulingMode, setSchedulingMode] = useState("");
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [stepError, setStepError] = useState("");
  const [submitAlternatives, setSubmitAlternatives] = useState({
    same_day: [],
    nearby: [],
  });
  const clearSubmitFeedback = () => {
    setSubmitError("");
    setStepError("");
    setSubmitAlternatives({ same_day: [], nearby: [] });
  };

  useEffect(() => {
    setStep(1);
    setSuggestionDay(toBusinessDateKey(new Date(), businessTimezone));
    setSuggestedSlots([]);
    setSelectedSlotStartTime("");
    setSchedulingMode("");
    setAvailabilityError("");
    setCustomerSearchTerm("");
    customerBrowseRequestRef.current += 1;
    setCustomerBrowseOpen(false);
    setCustomerBrowseItems([]);
    setCustomerBrowseLoading(false);
    setCustomerBrowseError("");
    setCustomerBrowseHasMore(false);
    clearSubmitFeedback();
  }, [businessTimezone, resetToken]);

  useEffect(() => {
    const nextScope = `${appointmentForm.service_id || ""}:${appointmentForm.staff_id || ""}`;
    if (schedulingScopeRef.current === nextScope) return;
    schedulingScopeRef.current = nextScope;
    setSchedulingMode("");
    setSuggestionDay(toBusinessDateKey(new Date(), businessTimezone));
    setSuggestedSlots([]);
    setSelectedSlotStartTime("");
    setAvailabilityError("");
    onFieldChange("start_time", "");
    onFieldChange("end_time", "");
  }, [appointmentForm.service_id, appointmentForm.staff_id, businessTimezone, onFieldChange]);

  useEffect(() => {
    const nextDay = toDateInputValue(appointmentForm.start_time);
    if (!nextDay) return;
    setSuggestionDay((prev) => (prev === nextDay ? prev : nextDay));
  }, [appointmentForm.start_time]);

  const activeCustomers = useMemo(() => {
    const byId = new Map();
    [...customers, ...customerBrowseItems].forEach((item) => {
      if (item?.id && item.deleted !== true) byId.set(item.id, item);
    });
    return Array.from(byId.values());
  }, [customerBrowseItems, customers]);
  const customer = useMemo(
    () => activeCustomers.find((item) => item.id === appointmentForm.customer_id),
    [activeCustomers, appointmentForm.customer_id]
  );
  const customerResults = useMemo(
    () => activeCustomers.filter((item) => matchesCustomerSearch(item, customerSearchTerm)),
    [activeCustomers, customerSearchTerm]
  );
  const hasCustomerSearch = normalizeCustomerSearch(customerSearchTerm).length >= CUSTOMER_SEARCH_MIN_LENGTH;
  const service = useMemo(
    () => services.find((item) => item.id === appointmentForm.service_id),
    [services, appointmentForm.service_id]
  );
  const staffMember = useMemo(
    () => staff.find((item) => item.id === appointmentForm.staff_id),
    [staff, appointmentForm.staff_id]
  );
  const serviceDurationMinutes = useMemo(() => {
    const raw = service?.duration_minutes ?? service?.durationMinutes ?? service?.duration ?? service?.minutes;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [service]);
  const isHistoricalAppointment = useMemo(
    () => historicalOutcomeEnabled && isHistoricalAppointmentEnd({
      endTime: appointmentForm.end_time,
      timeZone: businessTimezone,
    }),
    [appointmentForm.end_time, businessTimezone, historicalOutcomeEnabled]
  );

  const canGoStep2 =
    customerMode === "quick"
      ? Boolean(quickCustomerForm.name.trim())
      : Boolean(appointmentForm.customer_id);
  const ownerTimeValue = appointmentForm.start_time?.startsWith(`${suggestionDay}T`)
    ? appointmentForm.start_time.slice(11, 16)
    : "";
  const hasValidOwnerTimeStep = schedulingMode !== "manual" || isFiveMinuteTimeValue(ownerTimeValue);
  const canGoStep4 = Boolean(appointmentForm.start_time && appointmentForm.end_time && hasValidOwnerTimeStep);
  const canLoadSuggestions = Boolean(
    schedulingMode === "manual" &&
    appointmentForm.service_id &&
    appointmentForm.staff_id &&
    suggestionDay
  );
  const hasSubmitAlternatives =
    submitAlternatives.same_day.length > 0 || submitAlternatives.nearby.length > 0;

  const loadCustomerBrowsePage = async (offset, replace = false) => {
    const requestId = customerBrowseRequestRef.current + 1;
    customerBrowseRequestRef.current = requestId;
    setCustomerBrowseLoading(true);
    setCustomerBrowseError("");
    try {
      const page = await fetchCustomerArchivePage(offset);
      if (customerBrowseRequestRef.current !== requestId) return;
      setCustomerBrowseItems((current) => {
        const combined = replace ? page.items : [...current, ...page.items];
        const seen = new Set();
        return combined.filter((item) => {
          if (!item?.id || seen.has(item.id)) return false;
          seen.add(item.id);
          return true;
        });
      });
      setCustomerBrowseHasMore(page.hasMore);
    } catch {
      if (customerBrowseRequestRef.current !== requestId) return;
      if (replace) setCustomerBrowseItems([]);
      setCustomerBrowseError("Non riesco a caricare l’archivio. Riprova.");
    } finally {
      if (customerBrowseRequestRef.current === requestId) setCustomerBrowseLoading(false);
    }
  };

  const openCustomerBrowse = () => {
    setCustomerSearchTerm("");
    setCustomerBrowseOpen(true);
    loadCustomerBrowsePage(0, true);
  };

  const closeCustomerBrowse = () => {
    customerBrowseRequestRef.current += 1;
    setCustomerBrowseOpen(false);
    setCustomerBrowseLoading(false);
    setCustomerBrowseError("");
    requestAnimationFrame(() => customerSearchRef.current?.focus());
  };

  const selectExistingCustomer = (item) => {
    onFieldChange("customer_id", item.id);
    setCustomerSearchTerm(item.name || "");
    setCustomerBrowseOpen(false);
  };

  useEffect(() => {
    if (!customer && shouldFocusCustomerSearchRef.current) {
      shouldFocusCustomerSearchRef.current = false;
      customerSearchRef.current?.focus();
    }
  }, [customer]);

  useEffect(() => {
    setStepError("");
  }, [
    step,
    customerMode,
    appointmentForm.customer_id,
    appointmentForm.service_id,
    appointmentForm.staff_id,
    appointmentForm.start_time,
    appointmentForm.end_time,
    appointmentForm.historical_outcome,
    quickCustomerForm.name,
  ]);

  useEffect(() => {
    if (!isHistoricalAppointment && appointmentForm.historical_outcome) {
      onFieldChange("historical_outcome", "");
    }
  }, [appointmentForm.historical_outcome, isHistoricalAppointment, onFieldChange]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    stepTitleRef.current?.focus({ preventScroll: true });
  }, [step]);

  useEffect(() => {
    if (!canLoadSuggestions) {
      setSuggestedSlots([]);
      setAvailabilityError("");
      setAvailabilityLoading(false);
      return undefined;
    }

    let cancelled = false;
    const loadAvailability = async () => {
      setAvailabilityLoading(true);
      setAvailabilityError("");
      try {
        const response = await api.get("/availability", {
          params: {
            service_id: appointmentForm.service_id,
            staff_id: appointmentForm.staff_id,
            date: suggestionDay,
            slot_step_minutes: SLOT_STEP_MINUTES,
          },
        });
        if (cancelled) return;
        setSuggestedSlots(response.data?.available_slots || []);
      } catch (error) {
        if (cancelled) return;
        setSuggestedSlots([]);
        setAvailabilityError(
          error?.response?.data?.detail || "Impossibile caricare gli slot liberi suggeriti."
        );
      } finally {
        if (!cancelled) {
          setAvailabilityLoading(false);
        }
      }
    };

    loadAvailability();
    return () => {
      cancelled = true;
    };
  }, [appointmentForm.service_id, appointmentForm.staff_id, canLoadSuggestions, suggestionDay]);

  const applySuggestedSlot = (slot) => {
    setSelectedSlotStartTime(slot.start_time);
    onFieldChange("start_time", toBusinessDateTimeLocalValue(slot.start_time, businessTimezone), {
      forceAutoFillEndTime: true,
    });
    if (serviceDurationMinutes) {
      const endDate = new Date(new Date(slot.start_time).getTime() + serviceDurationMinutes * 60000);
      if (!Number.isNaN(endDate.getTime())) {
        onFieldChange("end_time", toBusinessDateTimeLocalValue(endDate, businessTimezone));
      }
    }
    clearSubmitFeedback();
    toast.success("Orario selezionato.");
  };

  const applyExactStartTime = (timeValue) => {
    setSelectedSlotStartTime("");
    if (!suggestionDay || !/^\d{2}:\d{2}$/.test(timeValue)) {
      onFieldChange("start_time", "");
      onFieldChange("end_time", "");
      return;
    }
    const startLocalValue = `${suggestionDay}T${timeValue}`;
    if (!isFiveMinuteTimeValue(timeValue)) {
      onFieldChange("start_time", startLocalValue);
      onFieldChange("end_time", "");
      clearSubmitFeedback();
      return;
    }
    onFieldChange("start_time", startLocalValue, { forceAutoFillEndTime: true });
    if (serviceDurationMinutes) {
      const startIso = businessLocalDateTimeToIso(startLocalValue, businessTimezone);
      const startDate = startIso ? new Date(startIso) : null;
      if (startDate && !Number.isNaN(startDate.getTime())) {
        const endDate = new Date(startDate.getTime() + serviceDurationMinutes * 60000);
        onFieldChange("end_time", toBusinessDateTimeLocalValue(endDate, businessTimezone));
      }
    }
    clearSubmitFeedback();
  };

  const findFirstAvailability = async () => {
    const response = await api.get("/availability/first", {
      params: {
        service_id: appointmentForm.service_id,
        staff_id: appointmentForm.staff_id,
        start_date: toBusinessDateKey(new Date(), businessTimezone),
        slot_step_minutes: SLOT_STEP_MINUTES,
      },
    });
    return response.data;
  };

  const handleSchedulingModeChange = (nextMode) => {
    setSchedulingMode(nextMode);
    setSuggestionDay(toBusinessDateKey(new Date(), businessTimezone));
    setSuggestedSlots([]);
    setSelectedSlotStartTime("");
    setAvailabilityError("");
    onFieldChange("start_time", "");
    onFieldChange("end_time", "");
    clearSubmitFeedback();
  };

  const applyAlternativeSlot = (slot) => {
    applySuggestedSlot(slot);
    setStep(4);
    toast.success("Slot alternativo selezionato.");
  };

  const renderAlternativeSection = (title, slots, prefix) => {
    if (slots.length === 0) {
      return null;
    }

    return (
      <div className="space-y-2">
        <p className="text-xs font-medium text-slate-700">{title}</p>
        <div className="space-y-2">
          {slots.map((slot) => (
            <button
              key={`${prefix}-${slot.start_time}-${slot.end_time}`}
              type="button"
              onClick={() => applyAlternativeSlot(slot)}
              className="w-full rounded-sm border border-amber-200 bg-white px-3 py-3 text-left text-xs text-slate-900"
              data-testid={`${prefix}-${slot.start_time}`}
            >
              <div className="font-medium">{formatDateTimeIT(slot.start_time)}</div>
              <div className="mt-1 text-slate-600">
                Fine: {formatDateTimeIT(slot.end_time)}
              </div>
              <div className="mt-1 text-slate-500">
                Staff: {staffMember?.name || "Non assegnato"}
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const handleSubmit = async () => {
    clearSubmitFeedback();
    if (customerMode === "quick" ? !quickCustomerForm.name.trim() : !appointmentForm.customer_id) {
      setStepError("Controlla i dati mancanti prima di creare l'appuntamento.");
      setStep(1);
      return;
    }
    const serviceStaffError = getServiceStaffError(appointmentForm);
    if (serviceStaffError) {
      setStepError(serviceStaffError);
      setStep(2);
      return;
    }
    if (!appointmentForm.start_time || !appointmentForm.end_time) {
      setStepError("Controlla i dati mancanti prima di creare l'appuntamento.");
      setStep(3);
      return;
    }
    if (isHistoricalAppointment && !appointmentForm.historical_outcome) {
      setStepError("Scegli come registrare l’appuntamento passato.");
      setStep(4);
      return;
    }
    await onSubmit({
      onSuccess: () => {},
      onError: ({ errorMessage, alternatives }) => {
        setSubmitError(errorMessage || DEFAULT_SUBMIT_ERROR);
        setSubmitAlternatives(alternatives || { same_day: [], nearby: [] });
      },
    });
  };

  return (
    <div
      className={`${standalone ? "block" : "md:hidden"} mobile-safe-bottom min-w-0 bg-white border border-slate-200 rounded-sm p-4 sm:p-6`}
      data-testid="appointment-wizard-mobile"
    >
      <h3 ref={stepTitleRef} tabIndex={-1} className="text-base font-semibold section-title outline-none">
        {step === 1 && "Cliente"}
        {step === 2 && "Servizio e operatrice"}
        {step === 3 && "Giorno, fascia e orario"}
        {step === 4 && "Riepilogo e conferma"}
      </h3>
      <p className="text-xs text-slate-500 mt-1">Step {step} di 4</p>
      {step === 1 && (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => {
                setCustomerBrowseOpen(false);
                onCustomerModeChange("existing");
              }}
              className={`rounded-sm h-10 text-sm font-medium border ${
                customerMode === "existing"
                  ? "bg-slate-900 border-slate-900 text-white"
                  : "bg-white border-slate-200 text-slate-900"
              }`}
              data-testid="wizard-mode-existing"
            >
              Esistente
            </button>
            <button
              type="button"
              onClick={() => {
                setCustomerBrowseOpen(false);
                onCustomerModeChange("quick");
              }}
              className={`rounded-sm h-10 text-sm font-medium border ${
                customerMode === "quick"
                  ? "bg-slate-900 border-slate-900 text-white"
                  : "bg-white border-slate-200 text-slate-900"
              }`}
              data-testid="wizard-mode-quick"
            >
              Nuovo cliente rapido
            </button>
          </div>
          {customerMode === "existing" ? (
            customer ? (
              <div
                className="rounded-sm border border-slate-200 bg-slate-50 p-3"
                aria-live="polite"
                data-testid="wizard-selected-customer"
              >
                <p className="text-xs font-medium text-slate-500">Cliente selezionato</p>
                <p className="mt-1 text-sm font-medium text-slate-900">{customer.name}</p>
                <p className="mt-1 break-all text-xs text-slate-600">{getCustomerContact(customer)}</p>
                <button
                  type="button"
                  onClick={() => {
                    shouldFocusCustomerSearchRef.current = true;
                    onFieldChange("customer_id", "");
                    setCustomerSearchTerm("");
                    setCustomerBrowseOpen(false);
                  }}
                  className="mt-3 min-h-10 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm font-medium text-slate-900"
                  data-testid="wizard-change-customer"
                >
                  Cambia cliente
                </button>
              </div>
            ) : (
              <div className="space-y-2" data-testid="wizard-customer-search">
                <span className="text-sm font-medium text-slate-900">
                  Cerca cliente esistente
                </span>
                {!customerBrowseOpen ? (
                  <>
                    <Command
                      label="Cerca cliente esistente"
                      shouldFilter={false}
                      className="rounded-sm border border-slate-200"
                    >
                      <CommandInput
                        ref={customerSearchRef}
                        value={customerSearchTerm}
                        onValueChange={setCustomerSearchTerm}
                        placeholder="Cerca per nome, email o telefono..."
                        data-testid="wizard-customer-search-input"
                      />
                      <CommandList data-testid="wizard-customer-results">
                        {!hasCustomerSearch ? (
                          <p className="px-3 py-4 text-sm text-slate-500" data-testid="wizard-customer-search-prompt">
                            Inserisci almeno 2 caratteri per cercare nell’archivio.
                          </p>
                        ) : customerResults.length === 0 ? (
                          <CommandEmpty data-testid="wizard-customer-empty">
                            Nessun cliente trovato. Puoi usare “Nuovo cliente rapido”.
                          </CommandEmpty>
                        ) : (
                          <CommandGroup>
                            {customerResults.map((item) => (
                              <CommandItem
                                key={item.id}
                                value={item.id}
                                onSelect={() => selectExistingCustomer(item)}
                                className="min-h-12 flex-col items-start gap-0.5 px-3 py-2"
                                data-testid={`wizard-customer-result-${item.id}`}
                              >
                                <span className="font-medium text-slate-900">{item.name}</span>
                                <span className="break-all text-xs text-slate-600">{getCustomerContact(item)}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                      </CommandList>
                    </Command>
                    <button
                      type="button"
                      onClick={openCustomerBrowse}
                      aria-expanded="false"
                      className="min-h-11 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-900"
                      data-testid="wizard-customer-browse-toggle"
                    >
                      Sfoglia archivio
                    </button>
                  </>
                ) : (
                  <div
                    className="space-y-2 rounded-sm border border-slate-200 bg-slate-50 p-2"
                    data-testid="wizard-customer-browse-list"
                  >
                    <div className="pb-1">
                      <button
                        type="button"
                        onClick={closeCustomerBrowse}
                        className="min-h-11 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-900"
                        data-testid="wizard-customer-browse-back-to-search"
                      >
                        Torna alla ricerca
                      </button>
                    </div>
                    {customerBrowseLoading && customerBrowseItems.length === 0 && (
                      <p className="px-2 py-3 text-sm text-slate-500">Caricamento archivio...</p>
                    )}
                    {customerBrowseError && (
                      <p className="px-2 py-3 text-sm text-rose-600" data-testid="wizard-customer-browse-error">
                        {customerBrowseError}
                      </p>
                    )}
                    {customerBrowseItems.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => selectExistingCustomer(item)}
                        className="min-h-12 w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-left"
                        data-testid={`wizard-customer-browse-item-${item.id}`}
                      >
                        <span className="block font-medium text-slate-900">{item.name}</span>
                        <span className="block break-all text-xs text-slate-600">{getCustomerContact(item)}</span>
                      </button>
                    ))}
                    {(customerBrowseHasMore || customerBrowseLoading) && (
                      <button
                        type="button"
                        onClick={() => loadCustomerBrowsePage(customerBrowseItems.length)}
                        disabled={customerBrowseLoading}
                        className="min-h-11 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm font-semibold disabled:opacity-60"
                        data-testid="wizard-customer-browse-load-more"
                      >
                        {customerBrowseLoading ? "Caricamento..." : "Carica altri clienti"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )
          ) : (
            <div className="space-y-3">
              <input
                type="text"
                value={quickCustomerForm.name}
                onChange={(event) => onQuickCustomerChange("name", event.target.value)}
                placeholder="Nome cliente rapido"
                className="w-full border border-slate-200 rounded-sm h-10 px-3"
                data-testid="wizard-quick-customer-name"
              />
              <input
                type="text"
                value={quickCustomerForm.phone}
                onChange={(event) => onQuickCustomerChange("phone", event.target.value)}
                placeholder="Telefono opzionale"
                className="w-full border border-slate-200 rounded-sm h-10 px-3"
                data-testid="wizard-quick-customer-phone"
              />
              <input
                type="email"
                value={quickCustomerForm.email}
                onChange={(event) => onQuickCustomerChange("email", event.target.value)}
                placeholder="Email opzionale"
                className="w-full border border-slate-200 rounded-sm h-10 px-3"
                data-testid="wizard-quick-customer-email"
              />
            </div>
          )}
          {stepError && (
            <p className="text-sm text-rose-600" data-testid="wizard-step-error">
              {stepError}
            </p>
          )}
          <button
            type="button"
            onClick={() => {
              if (!canGoStep2) {
                setStepError(
                  customerMode === "quick"
                    ? "Seleziona un cliente o inserisci un nome."
                    : "Seleziona un cliente o inserisci un nome."
                );
                return;
              }
              setStepError("");
              setStep(2);
            }}
            className="w-full bg-slate-900 text-white rounded-sm h-11 text-sm font-medium"
          >
            Continua
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="mt-4 space-y-3">
          <Select
            value={appointmentForm.service_id || ""}
            onValueChange={(value) => onFieldChange("service_id", value)}
          >
            <SelectTrigger data-testid="wizard-service-select">
              <SelectValue placeholder="Seleziona servizio" />
            </SelectTrigger>
            <SelectContent>
              {services.map((item) => (
                <SelectItem
                  key={item.id}
                  value={item.id}
                  data-testid={`wizard-service-option-${item.id}`}
                >
                  {item.name} · {formatCurrencyEUR(item.price)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={appointmentForm.staff_id || ""}
            onValueChange={(value) => onFieldChange("staff_id", value)}
          >
            <SelectTrigger data-testid="wizard-staff-select">
              <SelectValue placeholder="Seleziona operatrice" />
            </SelectTrigger>
            <SelectContent>
              {staff.map((item) => (
                <SelectItem
                  key={item.id}
                  value={item.id}
                  data-testid={`wizard-staff-option-${item.id}`}
                >
                  {item.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {stepError && (
            <p className="text-sm text-rose-600" data-testid="wizard-step-error">
              {stepError}
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-full bg-white border border-slate-200 rounded-sm h-11 text-sm font-medium"
            >
              Indietro
            </button>
            <button
              type="button"
              onClick={() => {
                const serviceStaffError = getServiceStaffError(appointmentForm);
                if (serviceStaffError) {
                  setStepError(serviceStaffError);
                  return;
                }
                setStepError("");
                setStep(3);
              }}
              className="w-full bg-slate-900 text-white rounded-sm h-11 text-sm font-medium"
            >
              Continua
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="mt-4 space-y-3">
          <AvailabilitySelector
            date={suggestionDay}
            onDateChange={(value) => {
              setSuggestionDay(value);
              setSelectedSlotStartTime("");
              onFieldChange("start_time", "");
              onFieldChange("end_time", "");
            }}
            slots={suggestedSlots}
            loading={availabilityLoading}
            error={availabilityError}
            selectedStartTime={selectedSlotStartTime}
            onSelectSlot={applySuggestedSlot}
            onFindFirstAvailability={findFirstAvailability}
            schedulingMode={schedulingMode}
            onSchedulingModeChange={handleSchedulingModeChange}
            slotStepMinutes={SLOT_STEP_MINUTES}
            testIdPrefix="wizard-availability"
            dateTestId="wizard-suggestion-day-input"
            exactTimeMode
            exactTimeValue={ownerTimeValue}
            onExactTimeChange={applyExactStartTime}
            dateLabel="Scegli il giorno dell’appuntamento"
          />
          {stepError && (
            <p className="text-sm text-rose-600" data-testid="wizard-step-error">
              {stepError}
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => {
                setStep(2);
              }}
              className="w-full bg-white border border-slate-200 rounded-sm h-11 text-sm font-medium"
            >
              Indietro
            </button>
            <button
              type="button"
              onClick={() => {
                if (!canGoStep4) {
                  setStepError(
                    schedulingMode === "manual" && ownerTimeValue && !hasValidOwnerTimeStep
                      ? "Scegli un orario a intervalli di 5 minuti."
                      : schedulingMode === "manual" && suggestionDay
                      ? "Seleziona l'orario."
                      : "Seleziona giorno e orario."
                  );
                  return;
                }
                clearSubmitFeedback();
                setStep(4);
              }}
              className="w-full bg-slate-900 text-white rounded-sm h-11 text-sm font-medium"
            >
              Riepilogo
            </button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="mt-4 space-y-3">
          {submitError && (
            <div
              className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-3 text-sm"
              data-testid="wizard-submit-error"
            >
              <p className="font-medium text-amber-900">Appuntamento non creato</p>
              <p className="mt-1 text-amber-900">{submitError}</p>
              <p className="mt-2 text-xs text-amber-800">
                Controlla orario, staff o scegli uno slot alternativo.
              </p>
            </div>
          )}
          {stepError && (
            <div
              className="rounded-sm border border-rose-200 bg-rose-50 px-3 py-3 text-sm"
              data-testid="wizard-step-error"
            >
              <p className="font-medium text-rose-900">Dati mancanti</p>
              <p className="mt-1 text-rose-900">{stepError}</p>
            </div>
          )}
          <div className="border border-slate-200 rounded-sm p-3 text-sm space-y-1">
            <p>
              <span className="text-slate-500">Cliente:</span>{" "}
              {customerMode === "quick" ? quickCustomerForm.name || "-" : customer?.name || "-"}
            </p>
            <p><span className="text-slate-500">Servizio:</span> {service?.name || "-"}</p>
            <p><span className="text-slate-500">Staff:</span> {staffMember?.name || "Non assegnato"}</p>
            <p><span className="text-slate-500">Inizio:</span> {formatDateTimeIT(appointmentForm.start_time)}</p>
            <p><span className="text-slate-500">Fine:</span> {formatDateTimeIT(appointmentForm.end_time)}</p>
          </div>
          {isHistoricalAppointment && (
            <fieldset
              className="rounded-sm border border-amber-200 bg-amber-50 p-3"
              data-testid="wizard-historical-outcome"
            >
              <legend className="px-1 text-sm font-semibold text-amber-950">
                Come vuoi registrare questo appuntamento passato?
              </legend>
              <p className="mt-1 text-xs text-amber-900">Scegli un esito prima di creare l’appuntamento.</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {[
                  ["completed", "Svolto"],
                  ["cancelled", "Annullato"],
                  ["no_show", "No-show"],
                  ["scheduled", "Da chiudere"],
                ].map(([value, label]) => {
                  const selected = appointmentForm.historical_outcome === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => onFieldChange("historical_outcome", value)}
                      className={`min-h-11 rounded-sm border px-3 py-2 text-sm font-semibold ${
                        selected
                          ? "border-amber-900 bg-amber-900 text-white"
                          : "border-amber-300 bg-white text-amber-950"
                      }`}
                      data-testid={`wizard-historical-outcome-${value}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}
          {hasSubmitAlternatives && (
            <div
              className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-3 space-y-3"
              data-testid="wizard-submit-alternatives"
            >
              <p className="text-sm font-medium text-slate-900">Slot alternativi disponibili</p>
              {renderAlternativeSection(
                "Disponibili oggi o nello stesso giorno",
                submitAlternatives.same_day,
                "wizard-submit-alt-same-day"
              )}
              {renderAlternativeSection(
                "Disponibili nei prossimi giorni",
                submitAlternatives.nearby,
                "wizard-submit-alt-nearby"
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => {
                setStep(3);
              }}
              className="w-full bg-white border border-slate-200 rounded-sm h-11 text-sm font-medium"
            >
              Indietro
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="w-full bg-slate-900 text-white rounded-sm h-11 text-sm font-medium"
              data-testid="wizard-submit-button"
            >
              Crea appuntamento
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
